import { useMemo, type ComponentProps } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import KatexFormula from './KatexFormula';
import { splitMessageRenderSegments } from './parseLatexFormulas';
import { isSuggestedQuestionLink } from './parseSuggestedQuestions';

type MarkdownWithMathProps = {
  content: string;
  markdownComponents: ComponentProps<typeof ReactMarkdown>['components'];
};

export default function MarkdownWithMath({ content, markdownComponents }: MarkdownWithMathProps) {
  const segments = useMemo(() => splitMessageRenderSegments(content), [content]);

  if (segments.length === 1 && segments[0]?.type === 'markdown') {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        urlTransform={url => (isSuggestedQuestionLink(url) ? url : defaultUrlTransform(url))}
        components={markdownComponents}
      >
        {segments[0].content}
      </ReactMarkdown>
    );
  }

  return (
    <>
      {segments.map((segment, index) => {
        if (segment.type === 'block-math') {
          return <KatexFormula key={`math-block-${index}`} latex={segment.content} display="block" />;
        }

        if (segment.type === 'inline-math') {
          return <KatexFormula key={`math-inline-${index}`} latex={segment.content} display="inline" />;
        }

        if (!segment.content.trim()) return null;

        return (
          <ReactMarkdown
            key={`markdown-${index}`}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            urlTransform={url => (isSuggestedQuestionLink(url) ? url : defaultUrlTransform(url))}
            components={markdownComponents}
          >
            {segment.content}
          </ReactMarkdown>
        );
      })}
    </>
  );
}
